import type { FilterFormType, IndependentAction, ViewType } from '@lighthouse/core'
import { FilterMode } from '@lighthouse/core'
import {
    FlowNode,
    getIsFindUseInFilter,
    useAtomAction,
    useAtomData,
    useFindUseObjectContext,
    ViewFieldFilterConfigure,
    Workflow
} from '@lighthouse/shared'
import React, { useCallback, useDeferredValue, useMemo } from 'react'

import {
    availableFieldBlocksAtom,
    availableFilterBlocksAtom,
    blockHighlightAtom,
    lastPageOfStackAtom,
    pagePathParameterFamily,
    pageQueryParameterFamily,
    pageStatesParameterFamily,
    pageVariableSchemasFamily
} from '@/atoms/page/state'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useCustomViewStatisticExtraFields } from '@/hooks/useCustomViewStatisticExtraFields'
import { useDataSourceList } from '@/hooks/useDataSource'
import { useIsDisabledWithVersion } from '@/hooks/useIsDisabledWithVersion'

import type { ListItemMenuProps } from './BlockSettings/Common/ListItemMenu'
import { ListItemMenu } from './BlockSettings/Common/ListItemMenu'

interface ConditionFilterProps extends Partial<ListItemMenuProps> {
    label: string
    currentBlockId?: string
    currentDsId?: string
    viewDsId?: string
    viewType?: ViewType
    isImage?: boolean
    formDsId?: string
    upstreamNodes?: FlowNode[]
    workflowList?: Workflow[]
    allParentActions?: IndependentAction[]
    filter: FilterFormType | undefined
    onFilter: (val: FilterFormType) => void
}

export const ConditionFilter: React.FC<ConditionFilterProps> = ({
    label,
    currentBlockId,
    currentDsId,
    formDsId,
    viewDsId,
    viewType,
    isImage,
    upstreamNodes,
    workflowList,
    allParentActions,
    filter,
    onFilter,
    ...rest
}) => {
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const pageId = useAtomData(
        lastPageOfStackAtom,
        useCallback(s => s?.pageId || '', [])
    )

    const findUseObject = useFindUseObjectContext()
    const { run: setBlockHighlight } = useAtomAction(blockHighlightAtom)
    const disabledWithVersion = useIsDisabledWithVersion()
    const dataSourceList = useDataSourceList(appId, envId)
    const extraViewFields = useCustomViewStatisticExtraFields()
    const availableFieldBlocksInfo = useDeferredValue(useAtomData(availableFieldBlocksAtom))

    const fieldBlocksWithDsIdWithoutCurrent = useMemo(() => {
        return availableFieldBlocksInfo.filter(item => item.block.id !== currentBlockId)
    }, [availableFieldBlocksInfo, currentBlockId])

    const availableFilterBlocksInfo = useDeferredValue(useAtomData(availableFilterBlocksAtom))
    const pageVariables = useAtomData(pageVariableSchemasFamily(pageId))
    const pagePathParameter = useAtomData(pagePathParameterFamily(pageId))
    const pageQueryParameter = useAtomData(pageQueryParameterFamily(pageId))
    const pageStateParameter = useAtomData(pageStatesParameterFamily(pageId))

    const isHighLight = getIsFindUseInFilter({
        findUseObject,
        data: filter,
        currentDsId
    })

    const handleMouseEnter = useCallback(
        (id: string) => {
            setBlockHighlight([id])
            document.querySelector(`div[data-node-id="${id}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        },
        [setBlockHighlight]
    )

    const handleMouseLeave = useCallback(() => {
        setBlockHighlight([])
    }, [setBlockHighlight])

    const filtersLength = useMemo(() => filter?.expression?.conditions?.length || 0, [filter?.expression?.conditions?.length])
    return (
        <ListItemMenu
            width="auto"
            style={{ backgroundColor: 'transparent' }}
            label={label}
            highlighting={isHighLight}
            positionDependencies={[filter]}
            value={filtersLength ? `已设 ${String(filtersLength)} 个条件` : '未设置'}
            popupContent={
                <ViewFieldFilterConfigure
                    dataSourceList={dataSourceList}
                    workflowList={workflowList}
                    availableFieldBlocksInfo={fieldBlocksWithDsIdWithoutCurrent}
                    availableFilterBlocksInfo={availableFilterBlocksInfo}
                    pagePathParameter={pagePathParameter}
                    pageQueryParameter={pageQueryParameter}
                    pageVariables={pageVariables}
                    pageStateParameter={pageStateParameter}
                    allParentActions={allParentActions}
                    upstreamNodes={upstreamNodes}
                    paramsMode={FilterMode.CUSTOM}
                    fieldMode={FilterMode.CUSTOM}
                    enableCurrentUser
                    formDsId={formDsId}
                    viewDsId={currentDsId || viewDsId}
                    viewType={viewType}
                    extraViewFields={extraViewFields}
                    isUseImage={isImage}
                    columns={[]}
                    filter={filter}
                    disabled={disabledWithVersion}
                    onFilter={onFilter}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                />
            }
            {...rest}
        />
    )
}
